import React, { useEffect, useState } from 'react'
import { Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, map, isNil } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  fetchClientTiers,
  fetchPammClientTiers
} from '../../api'
import { usePammRefId, useParamsObj, usePrevious } from '../../utils/hooks'
import { getUserValue, useGetPlatform } from '../utils'
import { CREATE_MASTER_ACCOUNT } from '../../../constants/routes'
import styles from './styles.css'

const getFormattedList = (lang) => map(
  ({ value, descriptionByLang, isEcn, labelByLang }) =>
    ({ label: labelByLang[lang] || labelByLang.en, value, descriptionByLang, isEcn })
)

/**
 * @typedef PricingListItem
 * @type {Object}
 * @property {string} label
 * @property {number} value
 * @property {Object.<string, string>} descriptionByLang
 * @property {boolean} isEcn
 */

const ExternalSelectPriceTier = ({ label, name, showLabelAsPlaceholder }) => {
  const { t, i18n } = useTranslation()
  const [{
    form,
    form: { accId },
    user,
    user: { brokerGroup, utmCampaign, deptCode, fundId }
  }, dispatch] = useStore()
  const { depositCurrency } = form[accId]
  const [value, setValue] = useState('')
  const [fetchError, setFetchError] = useState('')
  const [pricingList, setPricingList] = useState([])
  const [list, setList] = useState([])
  const [isValid, setIsValid] = useState(false)
  const [isUserSetValue, setIsUserSetValue] = useState(false)
  const location = useLocation()
  const prevDepositCurrency = usePrevious(depositCurrency)
  const lang = i18n.language
  const prevLang = usePrevious(i18n.language)
  const userValue = getUserValue(accId, user, name)
  const platform = useGetPlatform()
  const prevPlatform = usePrevious(platform)
  const paramsObj = useParamsObj(
    { brokerGroup, utmCampaign, deptCode, fundId, search: location.search, platform })
  const pammRef = usePammRefId()

  const setEnLabel = (value, pricingList) => {
    const item = pricingList?.find(({ value: listValue }) => listValue === Number(value))
    if (item) {
      dispatch({ type: 'setExtFieldValue', name: 'ctierEnLabel', value: item.labelByLang.en || '' })
    }
  }

  useEffect(() => {
    if (pammRef) {
      dispatch({
        type: 'setValidationFld',
        name,
        value: true
      })
      return
    }
    if (prevDepositCurrency === depositCurrency && prevLang === lang && prevPlatform === platform) {
      return
    }
    setList([])
    setFetchError('')
    if ((prevDepositCurrency && prevDepositCurrency !== depositCurrency) || (prevPlatform && prevPlatform !== platform)) {
      dispatch({ type: 'removeFieldValue', name })
      setValue('')
      setIsValid(false)
      dispatch({ type: 'setExtFieldValue', name: 'isEcn', value: false })
    }
    if (depositCurrency?.length) {
      const params = { ...paramsObj, depositCurrency }
      /**
       * @param {PricingListItem[]} pricingList
       */
      const fetchClientTiersCallback = (pricingList) => {
        if (!pricingList.length) return
        setFetchError('')
        setPricingList(pricingList)
        if (isUserSetValue) {
          setEnLabel(userValue, pricingList)
          return
        }
        if (pricingList.length === 1) {
          const defaultValueObj = pricingList.find(item => item.value === 0) || pricingList[0]
          const value = defaultValueObj.value.toString()
          setEnLabel(value, pricingList)
          dispatchValue(value)
          dispatchIsEcn(value, pricingList)
        } else if (prevDepositCurrency !== depositCurrency || pricingList.length > 1) {
          dispatch({ type: 'setValidationFld', name, value: false })
        }
      }
      const fetchFunction = location.pathname === CREATE_MASTER_ACCOUNT
        ? fetchPammClientTiers
        : fetchClientTiers
      fetchFunction(params).then(
        fetchClientTiersCallback,
        (error) => setFetchError(error.message))
    } else {
      dispatch({ type: 'removeValidationFld', name })
      dispatch({ type: 'removeFieldValue', name })
      setValue('')
      setIsValid(false)
    }
  }, [depositCurrency, isUserSetValue, paramsObj, pammRef])

  useEffect(() => {
    if (!isNil(userValue)) {
      dispatchValue(userValue.toString())
      setIsUserSetValue(true)
    }
  }, [userValue])

  useEffect(() => {
    setList(getFormattedList(lang)(pricingList))
  }, [pricingList, lang])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  const dispatchValue = value => {
    setValue(value)
    const hasValue = !!value.length
    setIsValid(hasValue)
    dispatch({
      type: 'setExtFieldValue',
      name,
      value: hasValue ? +value : null
    })

    dispatch({
      type: 'setValidationFld',
      name,
      value: hasValue
    })
  }

  const onChange = ({ target: { value } }) => {
    const selectedValue = getSelectValue(value)
    dispatchValue(selectedValue)
    dispatchIsEcn(value, list)
    setEnLabel(value, pricingList)
    setIsUserSetValue(false)
  }

  const dispatchIsEcn = (value, list) => {
    if (!value.length) {
      dispatch({ type: 'setExtFieldValue', name: 'isEcn', value: false })
    } else {
      const getItemListByValue = list.find(item => item.value === +value)
      dispatch({ type: 'setExtFieldValue', name: 'isEcn', value: !!getItemListByValue?.isEcn })
    }
  }

  const curTier = value.length ? list.find(item => item.value === +value) : null
  const additionalInfo = curTier
    ? curTier.descriptionByLang[i18n.language] || curTier.descriptionByLang.en
    : ''

  return (
    <>
      {list.length > 1 && !pammRef ? (
        <Select
          value={value || ''}
          label={t(label)}
          placeholder={t('generatedField.choose')}
          options={list}
          onChange={onChange}
          name={name}
          key={name}
          error=''
          valid={isValid}
          info={additionalInfo}
          showLabelAsPlaceholder={showLabelAsPlaceholder}
        />
      ) : null}
      {fetchError.length > 0 && <span className={styles.error}>{fetchError}</span>}
    </>
  )
}

const getExternalPriceTierField = networthFld => {
  const { i18n, name, required, order } = networthFld
  return (
    <ExternalSelectPriceTier
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      key={name}
      order={order}
    />
  )
}

export { ExternalSelectPriceTier, getExternalPriceTierField }
